import React, { useState, useCallback } from 'react';
import { uploadAndProcessImage } from '../../imageUploadUtils';
import api from '../../api';

// Symbols tab component
export const SymbolsTab = ({ symbols, onEditSymbol, onDeleteSymbol, onRowClick, onAdd }) => {
  const [filteredSymbols, setFilteredSymbols] = useState(symbols);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortField, setSortField] = useState("symbol_id");
  const [sortDirection, setSortDirection] = useState("asc");

  React.useEffect(() => {
    let result = [...symbols];

    // Apply type filter
    if (typeFilter !== "all") {
      result = result.filter(symbol => symbol.symbol_type === typeFilter);
    }

    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(symbol => {
        const translations = symbol.translations || {};
        return Object.values(translations).some(translation => {
          const name = typeof translation === 'object' ? translation.name : translation;
          return name?.toLowerCase().includes(searchLower);
        });
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredSymbols(result);
  }, [symbols, typeFilter, searchTerm, sortField, sortDirection]);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return "↕️";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const getUniqueTypes = () => {
    const types = symbols.map(s => s.symbol_type).filter(Boolean);
    return [...new Set(types)];
  };

  return (
    <div>
      <h2>Symbols</h2>
      <div style={{ marginBottom: "20px", display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button className="small-action-btn add" onClick={() => onAdd && onAdd()}>
          Add Symbol
        </button>
        <input
          type="text"
          placeholder="Search by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginRight: "10px", padding: "5px" }}
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{ padding: "5px" }}
        >
          <option value="all">All Types</option>
          {getUniqueTypes().sort().map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Symbol</th>
            <th onClick={() => handleSort("symbol_type")} style={{ cursor: "pointer" }}>
              Type <SortIcon field="symbol_type" />
            </th>
            <th>Translations</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredSymbols.map((symbol) => (
            <tr key={symbol.symbol_id} onClick={() => onRowClick && onRowClick('symbols', symbol)} style={{ cursor: 'pointer' }}>
              <td>{symbol.symbol_id}</td>
              <td>
                {symbol.symbol ? (
                  <img src={symbol.symbol} alt="Symbol" style={{ width: 48, height: 48, objectFit: 'contain' }} />
                ) : (
                  <span style={{ color: '#aaa' }}>No symbol</span>
                )}
              </td>
              <td>{symbol.symbol_type}</td>
              <td>
                {symbol.translations &&
                  Object.entries(symbol.translations).map(([lang, translation]) => (
                    <div key={lang}>
                      <b>{lang}:</b> {typeof translation === 'object' ? translation.name : translation}
                    </div>
                  ))}
              </td>
              <td>
                <button className="small-action-btn edit" onClick={(e) => { e.stopPropagation(); onEditSymbol(symbol); }}>Edit</button>
                <button className="small-action-btn delete" onClick={(e) => { e.stopPropagation(); onDeleteSymbol(symbol.symbol_id); }}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Add Symbol Form
export function AddSymbolForm({ onAdd, onCancel, languages }) {
  const [symbolType, setSymbolType] = useState("");
  const [translations, setTranslations] = useState({});
  const [symbolFile, setSymbolFile] = useState(null);
  const [uploadedSymbolUrl, setUploadedSymbolUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!symbolType.trim()) {
      alert("Symbol type is required");
      return;
    }

    // Check if at least one language has a name
    const hasAnyTranslation = Object.values(translations).some(trans => trans && trans.name);
    if (!hasAnyTranslation) {
      alert("At least one language must have a name");
      return;
    }

    setUploading(true);
    try {
      await onAdd({
        symbol_type: symbolType,
        symbol: uploadedSymbolUrl,
        translations: translations
      });
    } catch (err) {
      alert(err.response?.data?.error || "Failed to add symbol");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <form onSubmit={handleSubmit} style={{ marginBottom: "20px", padding: "20px", border: "1px solid #ccc", borderRadius: "20px" }}>
        <h3>Add Symbol</h3>

        <div style={{ marginBottom: "10px" }}>
          <label>Symbol Type:</label>
          <select
            value={symbolType}
            onChange={(e) => setSymbolType(e.target.value)}
            style={{ width: "400px", marginLeft: "10px" }}
          >
            <option value="">Select symbol type...</option>
            <option value="Card type">Card type</option>
            <option value="Deck">Deck</option>
            <option value="Emblem">Emblem</option>
          </select>
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Symbol File (SVG):</label><br />
          <input
            type="file"
            accept="image/svg+xml"
            onChange={async (e) => {
              try {
                const file = e.target.files[0];
                if (!file) return;
                setSymbolFile(file);
                // Create preview URL for the selected file
                const previewUrl = URL.createObjectURL(file);
                setUploadedSymbolUrl(previewUrl);
                const formData = new FormData();
                formData.append("symbol", file);
                const res = await api.post("/api/admin/symbols/upload", formData);
                setUploadedSymbolUrl(res.data.fileUrl);
                // Clean up the preview URL
                URL.revokeObjectURL(previewUrl);
              } catch (err) {
                alert(err.response?.data?.error || "Failed to upload symbol");
                setSymbolFile(null);
                setUploadedSymbolUrl(null);
                // Clean up the preview URL on error
                // Only revoke if it was set
                if (typeof previewUrl !== 'undefined') {
                  URL.revokeObjectURL(previewUrl);
                }
              }
            }}
            style={{ marginLeft: "10px" }}
          />
          {uploadedSymbolUrl && (
            <div style={{ marginTop: "10px", marginLeft: "10px" }}>
              <label>Preview:</label><br />
              <img
                src={uploadedSymbolUrl}
                alt="Symbol Preview"
                style={{ width: 48, height: 48, objectFit: 'contain', border: '1px solid #ccc' }}
              />
            </div>
          )}
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Translations:</label>
          <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
            Note: If English is not provided, the system will automatically translate from the first available language to English, then to all other languages.
          </div>
          {languages.map(lang => (
            <div key={lang.code} style={{ marginTop: "10px" }}>
              <h4>{lang.name} ({lang.code})</h4>
              <div style={{ marginLeft: "20px" }}>
                <div style={{ marginBottom: "5px" }}>
                  <label>Name:</label>
                  <input
                    type="text"
                    value={translations[lang.code]?.name || ""}
                    onChange={(e) => setTranslations(prev => ({
                      ...prev,
                      [lang.code]: {
                        ...prev[lang.code],
                        name: e.target.value
                      }
                    }))}
                    style={{ width: "400px", marginLeft: "10px" }}
                    placeholder="Optional (at least one language required)"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <button type="submit" disabled={uploading}>Add Symbol</button>
        <button type="button" onClick={onCancel} style={{ marginLeft: "10px" }}>Cancel</button>
      </form>
    </div>
  );
}

// Edit Symbol Form
export function EditSymbolForm({ symbol, onSave, onCancel, languages }) {
  const [symbolType, setSymbolType] = useState(symbol.symbol_type || "");
  const [translations, setTranslations] = useState(symbol.translations || {});
  const [symbolFile, setSymbolFile] = useState(null);
  const [uploadedSymbolUrl, setUploadedSymbolUrl] = useState(symbol.symbol || null);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!symbolType.trim()) {
      alert("Symbol type is required");
      return;
    }

    // Check if at least one language has a name
    const hasAnyTranslation = Object.values(translations).some(trans => trans && trans.name);
    if (!hasAnyTranslation) {
      alert("At least one language must have a name");
      return;
    }

    setUploading(true);
    try {
      await onSave({
        symbol_type: symbolType,
        symbol: uploadedSymbolUrl,
        translations: translations
      });
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update symbol");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <form onSubmit={handleSubmit} style={{ marginBottom: "20px", padding: "20px", border: "1px solid #ccc", borderRadius: "20px" }}>
        <h3>Edit Symbol</h3>

        <div style={{ marginBottom: "10px" }}>
          <label>Symbol Type:</label>
          <select
            value={symbolType}
            onChange={(e) => setSymbolType(e.target.value)}
            style={{ width: "400px", marginLeft: "10px" }}
          >
            <option value="">Select symbol type...</option>
            <option value="Card type">Card type</option>
            <option value="Deck">Deck</option>
            <option value="Symbol">Symbol</option>
          </select>
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Current Symbol:</label><br />
          {symbol.symbol ? (
            <img src={symbol.symbol} alt="Current Symbol" style={{ width: 48, height: 48, objectFit: 'contain', marginLeft: "10px" }} />
          ) : (
            <span style={{ color: '#aaa', marginLeft: "10px" }}>No symbol</span>
          )}
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>New Symbol File (SVG):</label><br />
          <input
            type="file"
            accept="image/svg+xml"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;

              setSymbolFile(file);

              // Create preview URL for the selected file
              const previewUrl = URL.createObjectURL(file);
              setUploadedSymbolUrl(previewUrl);

              const formData = new FormData();
              formData.append("symbol", file);
              try {
                const res = await api.post("/api/admin/symbols/upload", formData);
                setUploadedSymbolUrl(res.data.fileUrl);
                // Clean up the preview URL
                URL.revokeObjectURL(previewUrl);
              } catch (err) {
                alert(err.response?.data?.error || "Failed to upload symbol");
                setSymbolFile(null);
                setUploadedSymbolUrl(null);
                // Clean up the preview URL on error
                URL.revokeObjectURL(previewUrl);
              }
            }}
            style={{ marginLeft: "10px" }}
          />
          <div style={{ fontSize: "12px", color: "#666", marginLeft: "10px" }}>
            Leave empty to keep current symbol
          </div>
          {uploadedSymbolUrl && (
            <div style={{ marginTop: "10px", marginLeft: "10px" }}>
              <label>Preview:</label><br />
              <img
                src={uploadedSymbolUrl}
                alt="Symbol Preview"
                style={{ width: 48, height: 48, objectFit: 'contain', border: '1px solid #ccc' }}
              />
            </div>
          )}
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Translations:</label>
          <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
            Note: If English is not provided, the system will automatically translate from the first available language to English, then to all other languages.
          </div>
          {languages.map(lang => (
            <div key={lang.code} style={{ marginTop: "10px" }}>
              <h4>{lang.name} ({lang.code})</h4>
              <div style={{ marginLeft: "20px" }}>
                <div style={{ marginBottom: "5px" }}>
                  <label>Name:</label>
                  <input
                    type="text"
                    value={translations[lang.code]?.name || ""}
                    onChange={(e) => setTranslations(prev => ({
                      ...prev,
                      [lang.code]: {
                        ...prev[lang.code],
                        name: e.target.value
                      }
                    }))}
                    style={{ width: "400px", marginLeft: "10px" }}
                    placeholder="Optional (at least one language required)"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <button type="submit" disabled={uploading}>Save Changes</button>
        <button type="button" onClick={onCancel} style={{ marginLeft: "10px" }}>Cancel</button>
      </form>
    </div>
  );
}